import { useContext, useEffect, useMemo, useState } from 'react';
import '../../App.css';
import { Input } from '../ui/input';
import { DataTable } from './datatable';

import {
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Link, useNavigate } from 'react-router';
import { AuthContext } from '../../context/AuthContext';
import api from '../../lib/api';
import { Label } from '../ui/label';
import DatatableSortButton from './datatableSortButton';

export const columns = () => [
    {
        accessorKey: "name",
        header: ({ column }) => <DatatableSortButton column={column}>Namn</DatatableSortButton>,
        cell: info => {
            const competition = info.row.original;
            return <Link to={`/competition/${competition.competitionId}`} className='text-blue-600 font-medium hover:underline'>{competition.name}</Link>;
        }
    },
    {
        accessorKey: "startDate",
        header: ({ column }) => <DatatableSortButton column={column}>Startdatum</DatatableSortButton>,
        cell: info => {
            const competition = info.row.original;
            const date = new Date(competition.startDate);
            return date.toLocaleDateString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            });
        }
    },
    {
        accessorKey: "endDate",
        header: ({ column }) => <DatatableSortButton column={column}>Slutdatum</DatatableSortButton>,
        cell: info => {
            const competition = info.row.original;
            const date = new Date(competition.endDate);
            return date.toLocaleDateString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            });
        }
    },
];

export function MyCompetitionsDatatable({ userId }) {
    const [sorting, setSorting] = useState([{ id: "startDate", desc: false }]);
    const [competitions, setCompetitions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [columnFilters, setColumnFilters] = useState([]);
    const [filterInput, setFilterInput] = useState("");
    const { auth } = useContext(AuthContext);
    const navigate = useNavigate();

    const fetchCompetitions = async () => {
        try {
            const response = await api.get(`https://skhrf-api.cmdigital.se/v1/competitions/my`);
            const competitions = response.data.map(competition => ({
                ...competition,
            }));

            setCompetitions(competitions);
        } catch (error) {
            console.error('Error fetching competitions:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCompetitions();
    }, [userId]);

    const filteredCompetitions = useMemo(() => {

        const result = competitions.filter(competition => {
            // Handle competitions without an end date safely
            const endDate = competition.endDate ? new Date(competition.endDate) : null;

            // Log for debugging
            if (endDate) {

            } else {

            }

            // Filter by search input
            const matchesSearch = competition.name.toLowerCase().includes(filterInput.toLowerCase());

            // If checkbox is unchecked, include all competitions
            return matchesSearch;
        });

        return result;
    }, [competitions, filterInput]);

    // Delay function before filtering to reduce bounce
    useEffect(() => {
        const handler = setTimeout(() => {
            table.getColumn("name")?.setFilterValue(filterInput);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [filterInput]);

    const columnsMemoized = useMemo(() => columns(), []);

    const table = useReactTable({
        data: filteredCompetitions || [],
        columns: columnsMemoized,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            sorting,
            columnFilters,
        },
    });

    return (
        <>
            <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 py-3'>
                <div className='flex gap-3'>
                    <div className='flex flex-col gap-2 w-full'>
                        <Label htmlFor='searchCompetition'>Tävling</Label>
                        <Input
                            id='searchCompetition'
                            placeholder="Sök tävling..."
                            value={filterInput}
                            disabled={isLoading || filteredCompetitions.length === 0}
                            onChange={event => setFilterInput(event.target.value)}
                            className="max-w-sm"
                        />
                    </div>
                </div>
            </div>
            <DataTable
                table={table}
                isLoading={isLoading}
                columns={columnsMemoized}
                skeletonRows={5}
            />
        </>
    );
}
